import { logger } from '@belong/logging/logger';
import { BUTTON_VARIANTS, FONT_COLOURS } from '@belong/themes';
import { IButton, ICON_NAMES } from '@belong/types';
import { BUTTON_TEST_ID, LOADING_BUTTON_PROGRESS } from '../../../helpers/testIds';
import { A11yCopy } from '../../styles/Typography/A11yCopy';
import StyledButton, {
  ChildrenContainer,
  ContentContainer,
  FocusRing,
  LoadingAnimation,
  StyledIcon
} from './Button.styles';
import { ButtonSpinner } from './ButtonSpinner/ButtonSpinner';
import CONTENT from './content.json';
import { getButtonState } from './getButtonState';

export const ButtonBase = (props: IButton) => {
  const {
    ref,
    variant = 'primary',
    width = 'default',
    a11yLabel,
    ariaLabelledBy,
    label, // to exclude from HTML attributes as it is a parsing error for WCAG 2.0 validator
    children,
    isLoading,
    disabled,
    selected,
    isSmall,
    icon,
    target,
    currentSelected,
    hasSelectedIconColor = FONT_COLOURS.SUCCESS,
    withFocusRing = true,
    isSmallFontSize = false,
    hasIconLeft = false,
    iconOnly = false,
    isFootnoteLink = false,
    loadingContent = CONTENT.loading,
    loadingDescription = '',
    isInlineSpinnerLoading = false,
    ...otherProps
  } = props;

  const isSecondary = variant === BUTTON_VARIANTS.SECONDARY;
  const isQuaternary = variant === BUTTON_VARIANTS.QUATERNARY;
  const hasHref = 'href' in otherProps;

  if ('href' in otherProps && 'type' in otherProps && (otherProps as any).type === 'button') {
    // eslint-disable-next-line no-console
    console.warn(`type='button' on an anchor tag, this will cause display issues on Safari`);
  }

  if (iconOnly && !icon) {
    logger.warn(
      'iconOnly can only be set when icon is provided and variant is BUTTON_VARIANTS.PRIMARY || BUTTON_VARIANTS.SECONDARY'
    );
  }

  const testid = otherProps['data-testid'] || BUTTON_TEST_ID[variant.toUpperCase()];
  const { isPrimary, showIcon, hasAriaDisabledState, loadingDisabled } = getButtonState({
    variant,
    isLoading,
    disabled,
    icon,
    isInlineSpinnerLoading,
    hasHref
  });

  // If the link is a footnote link, we need to extract the hash from it to use as part of the described by
  let ariaDescribedby = otherProps['aria-describedby'];
  if (isFootnoteLink && 'href' in otherProps && typeof otherProps.href === 'string') {
    const [, hash] = otherProps.href.split('#');
    if (hash) {
      ariaDescribedby = `${ariaDescribedby ?? ''} ${hash}`.trim();
    }
  }

  return (
    <StyledButton
      ref={ref}
      variant={variant}
      width={width}
      className="button"
      {...otherProps}
      data-testid={testid}
      data-state={isLoading ? 'loading' : undefined}
      isSmall={isSmall}
      isSmallFontSize={isSmallFontSize}
      data-button-variant={variant}
      aria-label={a11yLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedby}
      target={target}
      isLoading={isLoading}
      // We disable when passed in, isSecondary and loading or loading when not using the spinner.
      disabled={disabled ?? loadingDisabled}
      // Used to style the button disabled, while not being disabled in the DOM during loading.
      hasAriaDisabledState={hasAriaDisabledState}
      aria-disabled={hasAriaDisabledState ?? undefined}
      tabIndex={hasHref && hasAriaDisabledState ? -1 : undefined}
      onClick={evt => {
        if (isLoading || hasAriaDisabledState) {
          evt.preventDefault();
          evt.stopPropagation();
          return;
        }

        return otherProps.onClick?.(evt);
      }}
      /** @DEPRECATED - selected and currentSelected are deprecated for Button and will be removed by design team */
      selected={selected}
      currentSelected={currentSelected}
      hasSelectedIconColor={hasSelectedIconColor}
      hasIconLeft={hasIconLeft}
      iconOnly={isPrimary || isSecondary ? iconOnly : false}
      isFootnoteLink={isFootnoteLink}
    >
      {isLoading && isPrimary && !isInlineSpinnerLoading && (
        <LoadingAnimation colour="black" isLightColor={otherProps.isLightColor} />
      )}

      {isLoading && !isInlineSpinnerLoading && (
        <A11yCopy>
          <progress data-testid={LOADING_BUTTON_PROGRESS} max={100}>
            {children}
          </progress>
        </A11yCopy>
      )}

      {isFootnoteLink && <A11yCopy>{CONTENT.footnote}</A11yCopy>}
      {/* Keep loading status visible to assistive tech during inline spinner loading. */}
      <ContentContainer aria-hidden={!!a11yLabel && !isInlineSpinnerLoading}>
        {isPrimary && (
          <ButtonSpinner
            autoFocus
            title={loadingContent}
            description={loadingDescription}
            isLightColor={otherProps.isLightColor}
            isInlineSpinnerLoading={isInlineSpinnerLoading}
          />
        )}
        {showIcon && iconOnly && (isPrimary || isSecondary) ? (
          // Icon only rendering for primary button
          <>
            <StyledIcon
              name={icon!}
              variant={BUTTON_VARIANTS.PRIMARY}
              iconOnly={iconOnly}
              data-testid={BUTTON_TEST_ID.BUTTON_ICON}
            />
            <ChildrenContainer className="iconOnly" data-testid={BUTTON_TEST_ID.BUTTON_CHILDREN}>
              {children}
            </ChildrenContainer>
          </>
        ) : (
          // Rendering for everything else
          <>
            {showIcon && hasIconLeft && (
              <StyledIcon
                className="icon"
                name={icon!}
                variant={BUTTON_VARIANTS.PRIMARY}
                data-testid={BUTTON_TEST_ID.BUTTON_ICON}
                hasIconLeft
              />
            )}
            {!isInlineSpinnerLoading && (
              <ChildrenContainer className="children" data-testid={BUTTON_TEST_ID.BUTTON_CHILDREN}>
                {children}
              </ChildrenContainer>
            )}
            {showIcon && !hasIconLeft && (
              <StyledIcon className="icon" name={icon!} variant={variant} data-testid={BUTTON_TEST_ID.BUTTON_ICON} />
            )}
            {/** @deprecated This is deprecated and unused. Design system team will remove */}
            {(selected || currentSelected) && (
              <StyledIcon
                className="icon"
                name={ICON_NAMES.Tick}
                variant={variant}
                data-testid={BUTTON_TEST_ID.BUTTON_ICON}
              />
            )}
          </>
        )}
      </ContentContainer>
      {!isQuaternary && withFocusRing && <FocusRing variant={variant} />}
    </StyledButton>
  );
};

ButtonBase.displayName = 'ButtonBase';

export const ButtonWithRef = ButtonBase;
